import type { FC, ReactNode } from "react";
import { useAppSearch } from "../../appSearchContext.js";
import {
  NavigationMenuItem,
  type NavigationMenuItemProps,
} from "./navigationMenuItem.js";

export interface NavigationMenuProps {
  items: readonly NavigationMenuItemProps[];
  /** Fallback active detection by label; per-item `isActive` takes precedence. */
  activeLabel?: string;
  /** Optional leading item, e.g. the search trigger. */
  search?: ReactNode;
}

export const NavigationMenu: FC<NavigationMenuProps> = ({
  items,
  activeLabel,
  search,
}) => {
  // While search is open it steals the active state from every other item.
  const { isSearchActive } = useAppSearch();

  return (
    <nav aria-label="Section navigation" className="min-h-0 flex-1">
      <ul className="flex flex-col gap-0.5">
        {search ? <li>{search}</li> : null}
        {items.map((item) => (
          <li key={item.label}>
            <NavigationMenuItem
              {...item}
              isActive={
                !isSearchActive && (item.isActive ?? item.label === activeLabel)
              }
            />
          </li>
        ))}
      </ul>
    </nav>
  );
};

/**
 * Whether `href` is the active nav destination for the current `pathname` —
 * true on an exact match or when `pathname` is nested beneath `href`.
 */
export function matchesNavPath(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}
